import {
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { BehaviorSubject, map, Observable, Subscription } from 'rxjs';

import { ResponsiveService } from 'app/services/responsive.service';
import { BrowseMode, ModeNamesEnum } from '../../models/browse-mode';
import { ModeStore } from '../../store/mode/mode.store';
import { MostSearchedTermsStore } from '../../store/most-searched-terms/most-searched-terms.store';
import { VideoStore } from '../../store/video/video.store';
import { BrowseTypesEnum } from '../../models/browse-types.enum';
import { CarouselItems } from '../../models/carousel-items.enum';
import { CatalogService } from '../../services/catalog.service';
import { TranslateModule } from '@ngx-translate/core';
import { CatalogLibraryComponent } from './catalog-library/catalog-library.component';
import { ListingComponent } from '../../components/listing/listing.component';
import { CatalogVideoComponent } from './catalog-video/catalog-video.component';
import { CatalogToolbarComponent } from './catalog-toolbar/catalog-toolbar.component';
import { HorizontalScrollerComponent } from '../../components/horizontal-scroller/horizontal-scroller.component';
import { SearchBarComponent } from '../../components/search-bar/search-bar.component';
import { NgClass, AsyncPipe } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';
import {
  ButtonComponent,
  ScenicCardComponent,
  User,
  VideoSection,
} from '@kinomap-dev/ng-ui-lib';
import { FavoriteApi } from '../../api/favorite.api';
import { Store } from '@ngrx/store';
import { selectProfileData } from 'app/store/me/me.selectors';
import { configureConnectionModal } from 'app/helpers/modal-helpers';
import { ModalService } from 'app/services/modal.service';
import { SwarmLoginService } from 'app/services/swarm-login.service';

@Component({
  selector: 'app-catalog',
  templateUrl: './catalog.component.html',
  styleUrls: ['./catalog.component.scss'],
  animations: [
    trigger('backgroundVideoFade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('400ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('400ms ease-in', style({ opacity: 0 }))]),
    ]),
  ],
  standalone: true,
  imports: [
    SearchBarComponent,
    NgClass,
    HorizontalScrollerComponent,
    CatalogToolbarComponent,
    CatalogVideoComponent,
    ListingComponent,
    CatalogLibraryComponent,
    AsyncPipe,
    TranslateModule,
    ButtonComponent,
    ScenicCardComponent,
    RouterLink,
  ],
})
export class CatalogComponent implements OnInit, OnDestroy {
  private router: Router = inject(Router);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private cd: ChangeDetectorRef = inject(ChangeDetectorRef);
  private catalogService: CatalogService = inject(CatalogService);
  private modeStore: ModeStore = inject(ModeStore);
  private videoStore: VideoStore = inject(VideoStore);
  private mostSearchedTermsStore: MostSearchedTermsStore = inject(
    MostSearchedTermsStore
  );
  private responsiveService: ResponsiveService = inject(ResponsiveService);
  private favoriteService: FavoriteApi = inject(FavoriteApi);
  private store: Store = inject(Store);
  private modal: ModalService = inject(ModalService);
  private loginService = inject(SwarmLoginService);
  private subscriptions: Subscription[] = [];
  private switchAutoPreviewInterval: any;
  private readonly switchAutoPreviewTimer: number = 3000;

  ContentTypes: typeof BrowseTypesEnum = BrowseTypesEnum;
  CarouselItems: typeof CarouselItems = CarouselItems;
  videosOfTheWeek: VideoSection[] = [];
  selectedVotw: VideoSection | null = null;
  width$: Observable<number>;
  searchedTerms$: Observable<string[]>;
  typedTerm: string = '';
  isSearchSubmitted: boolean = false;
  selectedMode$: Observable<BrowseMode>;
  ModeNames: typeof ModeNamesEnum = ModeNamesEnum;
  isToolbarFixed: boolean = false;
  isHomeMode: boolean = false;

  clientHasLog$: Observable<boolean> = this.store
    .select(selectProfileData)
    .pipe(map((user: User) => !!user));

  smallScreen$: BehaviorSubject<boolean> = this.responsiveService.smallScreen$;
  xSmallScreen$: BehaviorSubject<boolean> =
    this.responsiveService.xSmallScreen$;

  @HostListener('window:resize')
  onWindowResize(): void {
    this.fixOrUnfixToolbar();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.fixOrUnfixToolbar();
  }

  onClickSearchedTerm(term: string): void {
    this.typedTerm = term;
    this.isSearchSubmitted = true;
  }

  onSearchTerm(term: string): void {
    this.router.navigate(['search', term], {
      relativeTo: this.route,
    });
  }

  onSelectOrUnselectVideoOfTheWeek(videoId?: number): void {
    if (!videoId) {
      this.switchSelectedVotw();
      return;
    }

    this.resetSwitch();
    this.selectedVotw = this.videosOfTheWeek.find(
      (video: VideoSection) => video.id === videoId
    );
  }

  onActivateOrDeactivateHomeMode(isHome: boolean): void {
    this.isHomeMode = isHome;
    this.cd.detectChanges();
  }

  redirect(id: number, to: 'author' | 'browse'): Promise<boolean> {
    switch (to) {
      case 'author': {
        return this.router.navigate(['/dashboard/profile', id]);
      }
      case 'browse': {
        return this.router.navigate(['/browse/watch', id]);
      }
    }
  }

  onLikeClick(video: VideoSection): void {
    if (!video) return;

    this.subscriptions.push(
      this.clientHasLog$.subscribe((hasLog: boolean) => {
        if (!hasLog) {
          this.modal.reset();
          configureConnectionModal(this.modal, this.loginService);
          this.modal.display('connection-modal');
          return;
        }

        if (video.is_favorite) {
          this.subscriptions.push(
            this.favoriteService
              .deleteFavorite$(video.id, BrowseTypesEnum.Video)
              .subscribe(() => (video.is_favorite = false))
          );
          return;
        }
        this.subscriptions.push(
          this.favoriteService
            .postFavorite$(video.id, BrowseTypesEnum.Video)
            .subscribe(() => (video.is_favorite = true))
        );

        video.is_favorite = !video.is_favorite;
      })
    );
  }

  private switchSelectedVotw(): void {
    this.resetSwitch();

    const videoIds: number[] = this.videosOfTheWeek.map(
      (video: VideoSection) => video.id
    );

    let selectedId: number;

    if (!!this.selectedVotw) {
      selectedId = this.selectedVotw.id;
    } else {
      selectedId = videoIds[0];
      this.selectedVotw = this.videosOfTheWeek.find(
        (video: VideoSection) => video.id === selectedId
      );
    }

    this.switchAutoPreviewInterval = setInterval(() => {
      const index: number = videoIds.indexOf(selectedId);

      if (index < videoIds.length - 1) {
        selectedId = videoIds[index + 1];
      } else {
        selectedId = videoIds[0];
      }

      this.selectedVotw = this.videosOfTheWeek.find(
        (video: VideoSection) => video.id === selectedId
      );
    }, this.switchAutoPreviewTimer);

    this.cd.detectChanges();
  }

  private resetSwitch(): void {
    if (!!this.switchAutoPreviewInterval) {
      clearInterval(this.switchAutoPreviewInterval);
    }
  }

  private fixOrUnfixToolbar(): void {
    const scrollPosition: number = window.scrollY;
    const windowHeight: number = window.innerHeight;
    const navbarHeight: number = 70;
    const toolbarHeight: number = document
      .getElementById('catalogToolbar')
      .getElementsByClassName('catalog-toolbar')[0]
      .getBoundingClientRect().height;

    const scroll: number = scrollPosition;
    const height: number = windowHeight - navbarHeight - toolbarHeight;
    this.isToolbarFixed = height - scroll < 1;
  }

  private haveVotwChanged(newVotw: VideoSection[]): boolean {
    return JSON.stringify(newVotw) !== JSON.stringify(this.videosOfTheWeek);
  }

  ngOnInit(): void {
    this.width$ = this.responsiveService.width$;
    this.searchedTerms$ = this.mostSearchedTermsStore.mostSearchedTerms$;
    this.selectedMode$ = this.modeStore.selectedMode$;

    this.catalogService.subscribeToFetch();
    this.subscriptions.push(
      this.videoStore.videosOfTheWeek$.subscribe((votw: VideoSection[]) => {
        if (this.haveVotwChanged(votw)) {
          this.videosOfTheWeek = votw;
          this.selectedVotw = null;
          this.switchSelectedVotw();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.catalogService.unsubscribeToFetch();
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }
}
